import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  doc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import DashboardNav from "../components/DashboardNav";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Copy,
  CheckCircle2,
  Eye,
  EyeOff,
  Globe,
  Youtube,
  Tv,
  Twitter,
  Instagram,
  Gamepad2,
  Link as LinkIcon,
  Save,
} from "lucide-react";

const ICON_OPTIONS = [
  { value: "globe", label: "Link", icon: Globe },
  { value: "youtube", label: "YouTube", icon: Youtube },
  { value: "twitch", label: "Twitch", icon: Tv },
  { value: "twitter", label: "X / Twitter", icon: Twitter },
  { value: "instagram", label: "Instagram", icon: Instagram },
  { value: "tiktok", label: "TikTok", icon: Globe },
];

function EditLinksPage() {
  const navigate = useNavigate();
  const { currentUser, userProfile, refreshProfile } = useAuth();

  const [customLinks, setCustomLinks] = useState([]);
  const [externalGames, setExternalGames] = useState([]);
  const [campaignJoins, setCampaignJoins] = useState([]);
  const [hiddenCampaigns, setHiddenCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  // New link form
  const [newTitle, setNewTitle] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [newIcon, setNewIcon] = useState("globe");

  // New external game form
  const [newGameName, setNewGameName] = useState("");
  const [newGameUrl, setNewGameUrl] = useState("");

  useEffect(() => {
    if (userProfile) {
      setCustomLinks(userProfile.linkPageLinks || []);
      setExternalGames(userProfile.externalGames || []);
      setHiddenCampaigns(userProfile.linkPageHiddenCampaigns || []);
    }
  }, [userProfile]);

  useEffect(() => {
    async function fetchJoins() {
      if (!currentUser) return;
      try {
        const q = query(
          collection(db, "campaignJoins"),
          where("influencerId", "==", currentUser.uid)
        );
        const snap = await getDocs(q);
        const joins = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

        // Enrich with campaign status
        const enriched = [];
        for (const join of joins) {
          try {
            const campSnap = await getDoc(doc(db, "campaigns", join.campaignId));
            if (campSnap.exists()) {
              enriched.push({
                ...join,
                campaignStatus: campSnap.data().status,
                campaignCoverImageUrl: campSnap.data().coverImageUrl || join.campaignCoverImageUrl || "",
              });
            } else {
              enriched.push({ ...join, campaignStatus: "unknown" });
            }
          } catch {
            enriched.push({ ...join, campaignStatus: "unknown" });
          }
        }
        setCampaignJoins(enriched);
      } catch (err) {
        console.error("Failed to fetch joins:", err);
      }
      setLoading(false);
    }
    fetchJoins();
  }, [currentUser]);

  function handleAddLink() {
    if (!newUrl.trim()) return;
    let url = newUrl.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }
    setCustomLinks((prev) => [
      ...prev,
      { title: newTitle.trim(), url, icon: newIcon },
    ]);
    setNewTitle("");
    setNewUrl("");
    setNewIcon("globe");
  }

  function handleRemoveLink(index) {
    setCustomLinks((prev) => prev.filter((_, i) => i !== index));
  }

  function handleMoveLink(index, direction) {
    setCustomLinks((prev) => {
      const arr = [...prev];
      const target = index + direction;
      if (target < 0 || target >= arr.length) return arr;
      [arr[index], arr[target]] = [arr[target], arr[index]];
      return arr;
    });
  }

  function handleAddGame() {
    if (!newGameName.trim() || !newGameUrl.trim()) return;
    let url = newGameUrl.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }
    setExternalGames((prev) => [...prev, { name: newGameName.trim(), url }]);
    setNewGameName("");
    setNewGameUrl("");
  }

  function handleRemoveGame(index) {
    setExternalGames((prev) => prev.filter((_, i) => i !== index));
  }

  function toggleCampaignVisibility(campaignId) {
    setHiddenCampaigns((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId]
    );
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      await updateDoc(doc(db, "users", currentUser.uid), {
        linkPageLinks: customLinks,
        externalGames,
        linkPageHiddenCampaigns: hiddenCampaigns,
      });
      await refreshProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save links:", err);
    }
    setSaving(false);
  }

  function handleCopyLink() {
    const url = `https://gamesin.bio/${userProfile?.influencerProfile?.username || userProfile?.utmId || currentUser.uid}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  const linkPageUrl = `https://gamesin.bio/${userProfile?.influencerProfile?.username || userProfile?.utmId || currentUser?.uid}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 text-white">
        <DashboardNav />
        <div className="flex items-center justify-center py-32">
          <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <DashboardNav />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <button
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold mb-1">Your Link Page</h1>
            <p className="text-sm text-gray-500">
              Manage your public link page. Share it with your followers.
            </p>
          </div>
          <button
            onClick={() => window.open(linkPageUrl, "_blank")}
            className="flex items-center gap-2 text-sm text-brand-400 hover:text-brand-300 transition-colors"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
        </div>

        {/* Share URL */}
        <div className="bg-gray-900/50 border border-gray-800/50 rounded-2xl p-5 mb-8">
          <div className="flex items-center gap-2 mb-3">
            <LinkIcon className="w-4 h-4 text-brand-400" />
            <h3 className="text-sm font-medium">Your Link Page URL</h3>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 bg-gray-800/50 border border-gray-700/50 rounded-xl py-2.5 px-4 text-sm text-gray-300 truncate">
              {linkPageUrl}
            </div>
            <button
              onClick={handleCopyLink}
              className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 text-white px-4 py-2.5 rounded-xl transition-all text-sm font-medium flex-shrink-0"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy
                </>
              )}
            </button>
          </div>
        </div>

        {/* Campaign Links */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Gamepad2 className="w-4 h-4 text-brand-400" />
            <h2 className="text-base font-semibold">Campaign Links</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Active campaigns you've joined. Toggle visibility on your link page.
          </p>
          {campaignJoins.length > 0 ? (
            <div className="space-y-2">
              {campaignJoins.map((join) => {
                const isHidden = hiddenCampaigns.includes(join.campaignId);
                return (
                  <div
                    key={join.id}
                    className={`flex items-center gap-3 rounded-xl p-3 border transition-all ${
                      isHidden
                        ? "bg-gray-900/30 border-gray-800/30 opacity-50"
                        : "bg-gray-900/50 border-gray-800/50"
                    }`}
                  >
                    {join.campaignCoverImageUrl ? (
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={join.campaignCoverImageUrl}
                          alt={join.campaignTitle}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                        <Gamepad2 className="w-5 h-5 text-gray-600" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {join.campaignTitle}
                      </p>
                      <p className="text-xs text-gray-600">
                        {join.campaignStatus === "active" ? "Active" : "Paused"}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => toggleCampaignVisibility(join.campaignId)}
                      className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                        isHidden
                          ? "bg-gray-800 text-gray-500 border border-gray-700 hover:border-gray-600"
                          : "bg-green-500/10 text-green-400 border border-green-500/20 hover:border-green-500/40"
                      }`}
                    >
                      {isHidden ? (
                        <>
                          <EyeOff className="w-3.5 h-3.5" />
                          Hidden
                        </>
                      ) : (
                        <>
                          <Eye className="w-3.5 h-3.5" />
                          Visible
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-6 text-center">
              <p className="text-sm text-gray-500">
                No campaigns joined yet. Join campaigns to see them here.
              </p>
            </div>
          )}
        </div>

        {/* External Games */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Gamepad2 className="w-4 h-4 text-green-400" />
            <h2 className="text-base font-semibold">External Games</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Add games you want to promote that aren't part of a campaign.
          </p>

          {externalGames.length > 0 && (
            <div className="space-y-2 mb-4">
              {externalGames.map((game, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 bg-gray-900/50 border border-gray-800/50 rounded-xl p-3"
                >
                  <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                    <Gamepad2 className="w-5 h-5 text-green-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{game.name}</p>
                    <p className="text-xs text-gray-600 truncate">{game.url}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveGame(i)}
                    className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-3">Add a Game</h3>
            <div className="space-y-3">
              <input
                type="text"
                value={newGameName}
                onChange={(e) => setNewGameName(e.target.value)}
                placeholder="Game name (e.g. Elden Ring)"
                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl py-2.5 px-4 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              <input
                type="url"
                value={newGameUrl}
                onChange={(e) => setNewGameUrl(e.target.value)}
                placeholder="Link to game (e.g. Steam page URL)"
                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl py-2.5 px-4 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              <button
                type="button"
                onClick={handleAddGame}
                disabled={!newGameName.trim() || !newGameUrl.trim()}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-30 text-white px-4 py-2.5 rounded-xl transition-all text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Game
              </button>
            </div>
          </div>
        </div>

        {/* Custom Links */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <LinkIcon className="w-4 h-4 text-gray-400" />
            <h2 className="text-base font-semibold">Custom Links</h2>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Add any additional links to show on your page (merch, Discord, website, etc.).
          </p>

          {/* Existing custom links */}
          {customLinks.length > 0 && (
            <div className="space-y-2 mb-4">
              {customLinks.map((link, i) => {
                const IconComp =
                  ICON_OPTIONS.find((o) => o.value === link.icon)?.icon || Globe;
                return (
                  <div
                    key={i}
                    className="flex items-center gap-3 bg-gray-900/50 border border-gray-800/50 rounded-xl p-3"
                  >
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => handleMoveLink(i, -1)}
                        disabled={i === 0}
                        className="text-gray-600 hover:text-white disabled:opacity-20 transition-colors text-xs"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveLink(i, 1)}
                        disabled={i === customLinks.length - 1}
                        className="text-gray-600 hover:text-white disabled:opacity-20 transition-colors text-xs"
                      >
                        ▼
                      </button>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-gray-800 flex items-center justify-center text-gray-400 flex-shrink-0">
                      <IconComp className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {link.title || link.url}
                      </p>
                      <p className="text-xs text-gray-600 truncate">{link.url}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveLink(i)}
                      className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add new link form */}
          <div className="bg-gray-900/50 border border-gray-800/50 rounded-xl p-4">
            <h3 className="text-sm font-medium text-gray-300 mb-3">
              Add a Link
            </h3>
            <div className="space-y-3">
              {/* Icon selector */}
              <div className="flex flex-wrap gap-2">
                {ICON_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setNewIcon(opt.value)}
                    className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg font-medium transition-all ${
                      newIcon === opt.value
                        ? "bg-brand-500/20 text-brand-400 border border-brand-500/30"
                        : "bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600"
                    }`}
                  >
                    <opt.icon className="w-3.5 h-3.5" />
                    {opt.label}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Link title (e.g. My Discord Server)"
                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl py-2.5 px-4 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              <input
                type="url"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                placeholder="https://..."
                className="w-full bg-gray-800/50 border border-gray-700/50 rounded-xl py-2.5 px-4 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-brand-500/50 focus:ring-1 focus:ring-brand-500/50 transition-all"
              />
              <button
                type="button"
                onClick={handleAddLink}
                disabled={!newUrl.trim()}
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-30 text-white px-4 py-2.5 rounded-xl transition-all text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Add Link
              </button>
            </div>
          </div>
        </div>

        {/* Save button */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-6 py-3 rounded-xl transition-all font-semibold text-sm shadow-lg shadow-brand-600/25"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Changes
          </button>
          {saved && (
            <span className="flex items-center gap-1.5 text-sm text-green-400">
              <CheckCircle2 className="w-4 h-4" />
              Saved!
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditLinksPage;
